import React, { useMemo, useState, useCallback } from "react";
import { DataTable, Button, ActionButton, Tag } from "./ui";
import { MDBTooltip } from "mdb-react-ui-kit";
import { ChevronLeft, ChevronRight, List } from "lucide-react";
import { getImageUrl } from "../utils/utils";
import { posReadOnlyTooltip, isPosReadOnlyEntity } from "../utils/posReadOnly";
import { getLocalizedDisplayText } from "../utils/arabicTranslationUtils";
import { LocalizedReadOnlyText } from "./MissingTranslationBadge";
import ProductPriceWithApps from "./ProductPriceWithApps";
import "./CatalogItemsTable.css";

function ModifierPopover({
  popover,
  isArabic,
  t,
  canEditCatalog,
  catalog,
  rowPosLocked,
  onClose,
  onModifierIndexChange,
  onOptionIndexChange,
  onModifierAvailability,
}) {
  if (!popover) return null;

  const { item, modifierIndex, optionIndex, top, left } = popover;
  const modifiers = item.product.modifiers || [];
  const mod = modifiers[modifierIndex];
  if (!mod) return null;

  let isAvailable = true;
  if (
    item.catalogItemVisibility &&
    Array.isArray(item.catalogItemVisibility.modifier_availability)
  ) {
    const found = item.catalogItemVisibility.modifier_availability.find(
      (ma) => ma.modifierId === (mod.id || mod._id)
    );
    if (found) isAvailable = found.is_available;
  }

  const clampedLeft = Math.min(
    Math.max(12, left),
    window.innerWidth - 340
  );
  const clampedTop = Math.min(top, window.innerHeight - 320);

  return (
    <>
      <div
        className="catalog-modifier-popover__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="catalog-modifier-popover"
        style={{ top: clampedTop, left: clampedLeft }}
        role="dialog"
        aria-label={t("Modifier")}
      >
        <div className="catalog-modifier-popover__header">
          <div className="catalog-modifier-popover__title">
            <List size={16} aria-hidden="true" />
            <span>
              {t("Modifier")} ({modifierIndex + 1}/{modifiers.length})
            </span>
          </div>
          {modifiers.length > 1 && (
            <div className="catalog-modifier-popover__nav">
              <Button
                outline
                color="primary"
                iconOnly
                size="sm"
                onClick={() => {
                  onModifierIndexChange(modifierIndex - 1);
                  onOptionIndexChange(0);
                }}
                disabled={modifierIndex === 0}
                icon={isArabic ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              />
              <Button
                outline
                color="primary"
                iconOnly
                size="sm"
                onClick={() => {
                  onModifierIndexChange(modifierIndex + 1);
                  onOptionIndexChange(0);
                }}
                disabled={modifierIndex === modifiers.length - 1}
                icon={isArabic ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
              />
            </div>
          )}
        </div>

        <div
          className={`catalog-modifier-popover__card${
            isAvailable ? "" : " catalog-modifier-popover__card--unavailable"
          }`}
        >
          <div className="catalog-modifier-popover__card-header">
            <span className="catalog-modifier-popover__name">
              <LocalizedReadOnlyText
                {...getLocalizedDisplayText(
                  mod.name?.en,
                  mod.name?.ar,
                  isArabic,
                  "title"
                )}
              />
            </span>
            {!canEditCatalog || rowPosLocked(item) ? (
              <MDBTooltip
                tag="span"
                title={
                  rowPosLocked(item)
                    ? posReadOnlyTooltip(
                        t,
                        isPosReadOnlyEntity(item.product) ? item.product : catalog
                      )
                    : t("noPermissionToAccessThis")
                }
              >
                <span className="d-inline-flex">
                  <ActionButton
                    variant={isAvailable ? "inactive" : "active"}
                    disabled
                  >
                    {isAvailable ? t("markoutofstock") : t("markinstock")}
                  </ActionButton>
                </span>
              </MDBTooltip>
            ) : (
              <ActionButton
                variant={isAvailable ? "inactive" : "active"}
                onClick={() =>
                  onModifierAvailability(
                    { ...mod, id: mod.id || mod._id },
                    !isAvailable,
                    item.catalogV2ItemId
                  )
                }
              >
                {isAvailable ? t("markoutofstock") : t("markinstock")}
              </ActionButton>
            )}
          </div>

          {Array.isArray(mod.options) && mod.options.length > 0 && (
            <>
              <div className="catalog-modifier-popover__options-meta">
                <span>
                  {mod.options.length} {t("options")}
                  {mod.options.length > 3 && (
                    <span className="ms-1">
                      ({Math.min(optionIndex + 3, mod.options.length)}/
                      {mod.options.length})
                    </span>
                  )}
                </span>
                {mod.options.length > 3 && (
                  <div className="catalog-modifier-popover__nav">
                    <Button
                      outline
                      color="primary"
                      iconOnly
                      size="sm"
                      onClick={() =>
                        onOptionIndexChange(Math.max(0, optionIndex - 3))
                      }
                      disabled={optionIndex === 0}
                      icon={isArabic ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    />
                    <Button
                      outline
                      color="primary"
                      iconOnly
                      size="sm"
                      onClick={() => onOptionIndexChange(optionIndex + 3)}
                      disabled={optionIndex + 3 >= mod.options.length}
                      icon={isArabic ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                    />
                  </div>
                )}
              </div>
              {mod.options
                .slice(optionIndex, optionIndex + 3)
                .map((opt, oIdx) => (
                  <div
                    key={`${optionIndex}-${oIdx}`}
                    className="catalog-modifier-popover__option-row"
                  >
                    <span className="catalog-modifier-popover__option-name">
                      <LocalizedReadOnlyText
                        {...getLocalizedDisplayText(
                          opt.nameEn,
                          opt.nameAr,
                          isArabic,
                          "title"
                        )}
                      />
                    </span>
                    <span className="catalog-modifier-popover__option-price">
                      SAR {opt.price?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </>
  );
}

function CatalogItemsTable({
  sortedItems,
  selectedItems,
  setSelectedItems,
  canEditCatalog,
  canMutateCatalogLink,
  catalogReadOnly,
  catalog,
  rowPosLocked,
  isArabic,
  t,
  handleWeightChange,
  handleVisibility,
  handleModifierAvailability,
  handleEditItemClick,
  handleModalOpen,
  toolbarExtra,
  deliveryApps = [],
}) {
  const [modifierPopover, setModifierPopover] = useState(null);
  const [tableSortColumn, setTableSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const totalProductCount = useMemo(
    () =>
      sortedItems.reduce(
        (total, categoryGroup) => total + categoryGroup.products.length,
        0
      ),
    [sortedItems]
  );

  const allSelected =
    totalProductCount > 0 &&
    selectedItems.productIds.length === totalProductCount;

  const tableRows = useMemo(() => {
    const rows = [];
    sortedItems.forEach((categoryGroup) => {
      rows.push({
        _id: `category-${categoryGroup.category._id}`,
        rowType: "category",
        categoryGroup,
      });
      categoryGroup.products.forEach((item) => {
        rows.push({
          _id: item.product._id,
          rowType: "product",
          item,
          categoryGroup,
        });
      });
    });
    return rows;
  }, [sortedItems]);

  const displayRows = useMemo(() => {
    if (!tableSortColumn) return tableRows;
    const dir = sortDirection === "asc" ? 1 : -1;

    const getCategoryName = (categoryGroup) =>
      (isArabic
        ? categoryGroup.category.name?.ar
        : categoryGroup.category.name?.en) || "";

    if (tableSortColumn === "category") {
      const groups = [...sortedItems].sort(
        (a, b) => dir * getCategoryName(a).localeCompare(getCategoryName(b))
      );
      const rows = [];
      groups.forEach((categoryGroup) => {
        rows.push({
          _id: `category-${categoryGroup.category._id}`,
          rowType: "category",
          categoryGroup,
        });
        categoryGroup.products.forEach((item) => {
          rows.push({
            _id: item.product._id,
            rowType: "product",
            item,
            categoryGroup,
          });
        });
      });
      return rows;
    }

    const compareProducts = (a, b) => {
      let av;
      let bv;
      switch (tableSortColumn) {
        case "price":
          av = a.product.price ?? 0;
          bv = b.product.price ?? 0;
          return dir * (av - bv);
        case "weight":
          av = a.weight ?? 0;
          bv = b.weight ?? 0;
          return dir * (av - bv);
        case "stock":
          av = a.catalogItemVisibility?.is_visible ? 1 : 0;
          bv = b.catalogItemVisibility?.is_visible ? 1 : 0;
          return dir * (av - bv);
        default:
          av = (
            (isArabic ? a.product.name?.ar : a.product.name?.en) || ""
          ).toLowerCase();
          bv = (
            (isArabic ? b.product.name?.ar : b.product.name?.en) || ""
          ).toLowerCase();
          return dir * av.localeCompare(bv);
      }
    };

    const rows = [];
    sortedItems.forEach((categoryGroup) => {
      rows.push({
        _id: `category-${categoryGroup.category._id}`,
        rowType: "category",
        categoryGroup,
      });
      [...categoryGroup.products].sort(compareProducts).forEach((item) => {
        rows.push({
          _id: item.product._id,
          rowType: "product",
          item,
          categoryGroup,
        });
      });
    });
    return rows;
  }, [sortedItems, tableRows, tableSortColumn, sortDirection, isArabic]);

  const closeModifierPopover = useCallback(() => {
    setModifierPopover(null);
  }, []);

  const openModifierPopover = useCallback((item, anchorEl) => {
    const rect = anchorEl.getBoundingClientRect();
    setModifierPopover((prev) => {
      if (prev?.productId === item.product._id) return null;
      return {
        item,
        productId: item.product._id,
        modifierIndex: 0,
        optionIndex: 0,
        top: rect.bottom + 6,
        left: rect.left,
      };
    });
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "select",
        header: (
          <input
            type="checkbox"
            className="oh-table-checkbox"
            checked={allSelected}
            disabled={!canMutateCatalogLink || catalogReadOnly || totalProductCount === 0}
            onChange={(e) => {
              if (!canMutateCatalogLink || catalogReadOnly) return;
              if (e.target.checked) {
                const allIds = sortedItems.flatMap((categoryGroup) =>
                  categoryGroup.products.map((product) => product.product._id)
                );
                setSelectedItems({ productIds: allIds });
              } else {
                setSelectedItems({ productIds: [] });
              }
            }}
            aria-label="Select all items"
          />
        ),
        width: 52,
        hideable: false,
        render: (row) => {
          if (row.rowType === "category") return null;
          const { item } = row;
          const checked = selectedItems.productIds.includes(item.product._id);
          if (!canMutateCatalogLink || rowPosLocked(item)) {
            return (
              <MDBTooltip
                tag="span"
                title={
                  rowPosLocked(item)
                    ? posReadOnlyTooltip(
                        t,
                        isPosReadOnlyEntity(item.product) ? item.product : catalog
                      )
                    : t("noPermissionToAccessThis")
                }
              >
                <span>
                  <input
                    type="checkbox"
                    className="oh-table-checkbox"
                    checked={checked}
                    disabled
                    readOnly
                    aria-label={`Select item ${item.product.name?.en || item.product._id}`}
                  />
                </span>
              </MDBTooltip>
            );
          }
          return (
            <input
              type="checkbox"
              className="oh-table-checkbox"
              checked={checked}
              onChange={(e) => {
                if (!canMutateCatalogLink || catalogReadOnly) return;
                if (e.target.checked) {
                  setSelectedItems((prev) => ({
                    productIds: [...prev.productIds, item.product._id],
                  }));
                } else {
                  setSelectedItems((prev) => ({
                    productIds: prev.productIds.filter(
                      (pid) => pid !== item.product._id
                    ),
                  }));
                }
              }}
              aria-label={`Select item ${item.product.name?.en || item.product._id}`}
            />
          );
        },
      },
      {
        id: "weight",
        header: t("weigh/rank"),
        sortable: true,
        width: 90,
        render: (row) => {
          if (row.rowType === "category") {
            const { categoryGroup } = row;
            if (!canEditCatalog || catalogReadOnly) {
              return (
                <input
                  type="number"
                  className="form-control form-control-sm catalog-weight-input no-arrows"
                  value={categoryGroup.categoryWeight || ""}
                  disabled
                  readOnly
                />
              );
            }
            return (
              <input
                type="number"
                className="form-control form-control-sm catalog-weight-input no-arrows"
                value={categoryGroup.categoryWeight || ""}
                min="1"
                step="1"
                onKeyDown={(e) => {
                  if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  handleWeightChange(categoryGroup.category._id, null, value);
                }}
              />
            );
          }
          const { item } = row;
          if (!canEditCatalog) {
            return (
              <input
                type="number"
                className="form-control form-control-sm catalog-weight-input no-arrows"
                value={item.weight || ""}
                disabled
                readOnly
              />
            );
          }
          return (
            <input
              type="number"
              className="form-control form-control-sm catalog-weight-input no-arrows"
              value={item.weight || ""}
              min="1"
              step="1"
              disabled={rowPosLocked(item) || !canEditCatalog}
              onKeyDown={(e) => {
                if (["-", "+", "e", "E", "."].includes(e.key)) e.preventDefault();
              }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                handleWeightChange(null, item.product._id, value);
              }}
            />
          );
        },
      },
      {
        id: "category",
        header: t("category"),
        sortable: true,
        width: 160,
        render: (row) => {
          if (row.rowType === "category") {
            return (
              <span className="fw-semibold text-nowrap">
                <LocalizedReadOnlyText
                  {...getLocalizedDisplayText(
                    row.categoryGroup.category.name?.en,
                    row.categoryGroup.category.name?.ar,
                    isArabic,
                    "title"
                  )}
                />
              </span>
            );
          }
          return null;
        },
      },
      {
        id: "image",
        header: t("image"),
        width: 90,
        render: (row) => {
          if (row.rowType === "category") return null;
          const { item } = row;
          return (
            <img
              src={getImageUrl(item.product.image_path)}
              alt={item.product.name?.en || t("product")}
              className="rounded"
              style={{ width: 48, height: 48, objectFit: "cover" }}
            />
          );
        },
      },
      {
        id: "name",
        header: t("name"),
        sortable: true,
        width: 260,
        render: (row) => {
          if (row.rowType === "category") return null;
          const { item } = row;
          const modifierCount = item.product.modifiers?.length || 0;
          return (
            <div className="min-w-0">
              <div className="d-flex align-items-center gap-2 flex-nowrap">
                <span className="fw-semibold text-truncate">
                  <LocalizedReadOnlyText
                    {...getLocalizedDisplayText(
                      item.product.name?.en,
                      item.product.name?.ar,
                      isArabic,
                      "title"
                    )}
                  />
                </span>
                {modifierCount > 0 && (
                  <ActionButton
                    variant="branches"
                    showIcon={false}
                    className="catalog-modifier-trigger"
                    onClick={(e) => openModifierPopover(item, e.currentTarget)}
                  >
                    {t("Modifier")} ({modifierCount})
                  </ActionButton>
                )}
              </div>
              <div className="text-muted small text-truncate">
                {item.product.product_id}
              </div>
            </div>
          );
        },
      },
      {
        id: "price",
        header: t("price"),
        sortable: true,
        width: 110,
        align: "end",
        render: (row) => {
          if (row.rowType === "category") return null;
          return (
            <ProductPriceWithApps
              product={row.item.product}
              deliveryApps={deliveryApps}
              iconSize={12}
              color="#030f1b"
            />
          );
        },
      },
      {
        id: "stock",
        header: t("stock"),
        sortable: true,
        width: 120,
        render: (row) => {
          if (row.rowType === "category") return null;
          const visible = row.item.catalogItemVisibility?.is_visible;
          return (
            <Tag variant={visible ? "success" : "warning"}>
              {visible ? t("instock") : t("outofstock")}
            </Tag>
          );
        },
      },
      {
        id: "visibility",
        header: t("visibility"),
        width: 150,
        hideable: false,
        render: (row) => {
          if (row.rowType === "category") return null;
          const { item } = row;
          const visible = item.catalogItemVisibility?.is_visible;
          if (!canEditCatalog || rowPosLocked(item)) {
            return (
              <MDBTooltip
                tag="span"
                title={
                  rowPosLocked(item)
                    ? posReadOnlyTooltip(
                        t,
                        isPosReadOnlyEntity(item.product) ? item.product : catalog
                      )
                    : t("noPermissionToAccessThis")
                }
              >
                <span className="d-inline-flex">
                  <ActionButton
                    variant={visible ? "inactive" : "active"}
                    disabled
                  >
                    {visible ? t("markoutofstock") : t("markinstock")}
                  </ActionButton>
                </span>
              </MDBTooltip>
            );
          }
          return (
            <ActionButton
              variant={visible ? "inactive" : "active"}
              onClick={() => handleVisibility(item.product, !visible)}
            >
              {visible ? t("markoutofstock") : t("markinstock")}
            </ActionButton>
          );
        },
      },
      {
        id: "actions",
        header: t("actions"),
        width: 160,
        render: (row) => {
          if (row.rowType === "category") return null;
          const { item } = row;
          if (rowPosLocked(item)) {
            return (
              <div className="d-flex flex-nowrap gap-2">
                <MDBTooltip
                  tag="span"
                  title={posReadOnlyTooltip(
                    t,
                    isPosReadOnlyEntity(item.product) ? item.product : catalog
                  )}
                >
                  <span className="d-inline-flex">
                    <ActionButton variant="view" disabled>
                      {t("view")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
                <MDBTooltip
                  tag="span"
                  title={posReadOnlyTooltip(
                    t,
                    isPosReadOnlyEntity(item.product) ? item.product : catalog
                  )}
                >
                  <span className="d-inline-flex">
                    <ActionButton variant="delete" disabled>
                      {t("remove")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              </div>
            );
          }
          return (
            <div className="d-flex flex-nowrap gap-2">
              <ActionButton
                variant="view"
                onClick={() => handleEditItemClick(item.product)}
              >
                {t("view")}
              </ActionButton>
              {!canMutateCatalogLink ? (
                <MDBTooltip tag="span" title={t("noPermissionToAccessThis")}>
                  <span className="d-inline-flex">
                    <ActionButton variant="delete" disabled>
                      {t("remove")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <ActionButton
                  variant="delete"
                  onClick={() => handleModalOpen(item.product._id)}
                >
                  {t("remove")}
                </ActionButton>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      isArabic,
      sortedItems,
      selectedItems,
      allSelected,
      totalProductCount,
      canEditCatalog,
      canMutateCatalogLink,
      catalogReadOnly,
      catalog,
      rowPosLocked,
      handleWeightChange,
      handleVisibility,
      handleEditItemClick,
      handleModalOpen,
      openModifierPopover,
      setSelectedItems,
    ]
  );

  return (
    <>
      <DataTable
        tableId="catalog-details-items"
        title={t("items")}
        toolbarExtra={toolbarExtra}
        hideFooter
        columns={columns}
        data={displayRows}
        rowKey="_id"
        getRowClassName={(row) =>
          row.rowType === "category" ? "oh-table-row--category" : ""
        }
        emptyState={`${t("No items found")}.`}
        className="catalog-items-table"
        isRtl={isArabic}
        sort={{
          column: tableSortColumn,
          direction: sortDirection,
          onChange: ({ column, direction }) => {
            setTableSortColumn(column);
            setSortDirection(direction);
          },
        }}
      />
      <ModifierPopover
        popover={modifierPopover}
        isArabic={isArabic}
        t={t}
        canEditCatalog={canEditCatalog}
        catalog={catalog}
        rowPosLocked={rowPosLocked}
        onClose={closeModifierPopover}
        onModifierIndexChange={(index) =>
          setModifierPopover((prev) =>
            prev ? { ...prev, modifierIndex: index, optionIndex: 0 } : prev
          )
        }
        onOptionIndexChange={(index) =>
          setModifierPopover((prev) =>
            prev ? { ...prev, optionIndex: index } : prev
          )
        }
        onModifierAvailability={handleModifierAvailability}
      />
    </>
  );
}

export default CatalogItemsTable;
